export interface DatabaseDebugResult {
  connectionTest: boolean;
  tablesExist: boolean;
  rawDataCount: {
    users: number;
    pickLists: number;
    pickItems: number;
  };
  rlsStatus: {
    usersRLS: boolean;
    pickListsRLS: boolean;
    pickItemsRLS: boolean;
  };
  authenticationState: {
    hasSupabaseSession: boolean;
    appUserExists: boolean;
    userIdMatch: boolean;
  };
}

export class DatabaseDebugger {
  async runComprehensiveDebug(): Promise<DatabaseDebugResult> {
    const { supabase } = await import('../services/supabase');
    const { useAuthStore } = await import('../stores/authStore');
    
    console.log('🔍 Starting comprehensive database debug...');
    
    // 1. Test basic connection
    const connectionTest = await this.testConnection();
    console.log('📡 Connection test:', connectionTest);
    
    // 2. Check if tables exist
    const tablesExist = await this.checkTablesExist();
    console.log('📋 Tables exist:', tablesExist);
    
    // 3. Get raw data counts (bypass RLS)
    const rawDataCount = await this.getRawDataCounts();
    console.log('📊 Raw data counts:', rawDataCount);
    
    // 4. Check RLS status
    const rlsStatus = await this.checkRLSStatus();
    console.log('🔒 RLS status:', rlsStatus);
    
    // 5. Check authentication state
    const authenticationState = await this.checkAuthenticationState();
    console.log('🔑 Auth state:', authenticationState);
    
    return {
      connectionTest,
      tablesExist,
      rawDataCount,
      rlsStatus,
      authenticationState
    };
  }
  
  private async testConnection(): Promise<boolean> {
    try {
      const { supabase } = await import('../services/supabase');
      const { data, error } = await supabase.from('users').select('count').limit(1);
      return !error;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }
  
  private async checkTablesExist(): Promise<boolean> {
    try {
      const { supabase } = await import('../services/supabase');
      
      // Try to query each table
      const tables = ['users', 'pick_lists', 'pick_items'];
      const results = await Promise.all(
        tables.map(async (table) => {
          try {
            const { error } = await supabase.from(table).select('*').limit(1);
            return !error;
          } catch {
            return false;
          }
        })
      );
      
      return results.every(result => result);
    } catch {
      return false;
    }
  }
  
  private async getRawDataCounts(): Promise<{ users: number; pickLists: number; pickItems: number }> {
    try {
      const { supabase } = await import('../services/supabase');
      
      // Use service role to bypass RLS
      const { data: users } = await supabase.from('users').select('*');
      const { data: pickLists } = await supabase.from('pick_lists').select('*');
      const { data: pickItems } = await supabase.from('pick_items').select('*');
      
      return {
        users: users?.length || 0,
        pickLists: pickLists?.length || 0,
        pickItems: pickItems?.length || 0
      };
    } catch (error) {
      console.error('Raw data count failed:', error);
      return { users: 0, pickLists: 0, pickItems: 0 };
    }
  }
  
  private async checkRLSStatus(): Promise<{ usersRLS: boolean; pickListsRLS: boolean; pickItemsRLS: boolean }> {
    // This would require admin access to check RLS policies
    // For now, return assumed status based on schema
    return {
      usersRLS: true,
      pickListsRLS: true,
      pickItemsRLS: true
    };
  }
  
  private async checkAuthenticationState(): Promise<{
    hasSupabaseSession: boolean;
    appUserExists: boolean;
    userIdMatch: boolean;
  }> {
    try {
      const { supabase } = await import('../services/supabase');
      const { useAuthStore } = await import('../stores/authStore');
      
      // Check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      const hasSupabaseSession = !!session;
      
      // Check app user
      const appUser = useAuthStore.getState().user;
      const appUserExists = !!appUser;
      
      // Check if IDs match
      const userIdMatch = session?.user?.id === appUser?.id;
      
      return {
        hasSupabaseSession,
        appUserExists,
        userIdMatch
      };
    } catch (error) {
      console.error('Auth state check failed:', error);
      return {
        hasSupabaseSession: false,
        appUserExists: false,
        userIdMatch: false
      };
    }
  }
  
  async insertTestDataDirectly(): Promise<boolean> {
    try {
      const { supabase } = await import('../services/supabase');
      
      console.log('🚀 Attempting direct data insertion...');
      
      // 1. Insert user first
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert({
          id: '550e8400-e29b-41d4-a716-446655440000',
          email: 'picker@warehouse.com',
          firstname: 'Demo',
          lastname: 'Picker',
          pin: '1234',
          role: 'picker'
        })
        .select();
      
      if (userError) {
        console.error('❌ User insertion failed:', userError);
        return false;
      }
      
      console.log('✅ User inserted:', userData);
      
      // 2. Insert pick lists
      const pickListsData = [
        {
          id: '11111111-1111-1111-1111-111111111111',
          order_number: 'ORD-2024-001',
          status: 'assigned',
          assigned_to: '550e8400-e29b-41d4-a716-446655440000',
          priority: 'high',
          customer_name: 'ABC Electronics Store',
          total_items: 3,
          picked_items: 0,
          estimated_time: 15
        },
        {
          id: '22222222-2222-2222-2222-222222222222',
          order_number: 'ORD-2024-002',
          status: 'assigned',
          assigned_to: '550e8400-e29b-41d4-a716-446655440000',
          priority: 'medium',
          customer_name: 'Office Supplies Plus',
          total_items: 5,
          picked_items: 0,
          estimated_time: 25
        },
        {
          id: '33333333-3333-3333-3333-333333333333',
          order_number: 'ORD-2024-003',
          status: 'assigned',
          assigned_to: '550e8400-e29b-41d4-a716-446655440000',
          priority: 'low',
          customer_name: 'MegaMart Grocery',
          total_items: 8,
          picked_items: 0,
          estimated_time: 40
        }
      ];
      
      const { data: pickListData, error: pickListError } = await supabase
        .from('pick_lists')
        .insert(pickListsData)
        .select();
      
      if (pickListError) {
        console.error('❌ Pick list insertion failed:', pickListError);
        return false;
      }
      
      console.log('✅ Pick lists inserted:', pickListData);
      
      // 3. Insert pick items
      const pickItemsData = [
        // Items for ORD-2024-001
        {
          pick_list_id: '11111111-1111-1111-1111-111111111111',
          product_sku: 'ELEC-001',
          product_name: 'Wireless Mouse',
          quantity_required: 2,
          bin_location: 'A1-B2-L3'
        },
        {
          pick_list_id: '11111111-1111-1111-1111-111111111111',
          product_sku: 'ELEC-002',
          product_name: 'USB Cable',
          quantity_required: 5,
          bin_location: 'A1-B3-L1'
        },
        {
          pick_list_id: '11111111-1111-1111-1111-111111111111',
          product_sku: 'ELEC-003',
          product_name: 'Phone Charger',
          quantity_required: 1,
          bin_location: 'A2-B1-L2'
        },
        // Items for ORD-2024-002
        {
          pick_list_id: '22222222-2222-2222-2222-222222222222',
          product_sku: 'OFF-001',
          product_name: 'Copy Paper',
          quantity_required: 3,
          bin_location: 'B1-A1-L1'
        },
        {
          pick_list_id: '22222222-2222-2222-2222-222222222222',
          product_sku: 'OFF-002',
          product_name: 'Blue Pens',
          quantity_required: 2,
          bin_location: 'B1-A2-L3'
        }
      ];
      
      const { data: pickItemData, error: pickItemError } = await supabase
        .from('pick_items')
        .insert(pickItemsData)
        .select();
      
      if (pickItemError) {
        console.error('❌ Pick item insertion failed:', pickItemError);
        return false;
      }
      
      console.log('✅ Pick items inserted:', pickItemData);
      
      return true;
    } catch (error) {
      console.error('❌ Direct insertion failed:', error);
      return false;
    }
  }
}

export const databaseDebugger = new DatabaseDebugger();